import React, { useState } from "react";
import { PacmanLoader } from "react-spinners";
import { Row, Col, Button, ListGroup, Alert, Modal } from "react-bootstrap";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import { createColumnHelper } from "@tanstack/react-table";
import "containers/common/List.css";
import "containers/common/Table.css";

function BuildingNameList({
    data,
    isLoading,
    isAuthenticated,
    controlType,
    saveRecord,
    deleteRecord,
    updateTableState,
    filter,
    user,
}) {
    const dataSet = "building_names";
    const tableData = data || [];
    const columnHelper = createColumnHelper();
    const [showAlert, setShowAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    function showValidationMessage(message) {
        setErrorMessage(message);
        setShowAlert(true);
    }

    function validateRows(rows) {
        for (const row of rows) {
            if (!String(row?.building_name || "").trim()) {
                return "Building Name cannot be blank. The record has not been saved.";
            }
        }

        const seenSortOrders = new Set();
        for (const row of rows) {
            const rawSortOrder = row?.sort_order;
            if (rawSortOrder === null || rawSortOrder === undefined || rawSortOrder === "") {
                continue;
            }

            const normalizedSortOrder = Number(rawSortOrder);
            if (Number.isNaN(normalizedSortOrder)) {
                continue;
            }

            if (seenSortOrders.has(normalizedSortOrder)) {
                return "Duplicate sort orders. The record has not been saved.";
            }

            seenSortOrders.add(normalizedSortOrder);
        }

        return null;
    }

    function handleDeleteRow(row, event) {
        event.preventDefault();
        const confirmed = window.confirm("Are you sure you want to delete this record?");
        if (!confirmed) {
            return;
        }
        deleteRecord(dataSet, row.original);
    }

    const columns = [
        columnHelper.accessor("building_name_id", {
            id: "building_name_id",
            header: "ID",
            size: 60,
        }),
        columnHelper.accessor("building_name", {
            id: "building_name",
            header: "Building Name",
            cell: EditableCell,
        }),
        columnHelper.accessor("sort_order", {
            id: "sort_order",
            header: "Sort Order",
            cell: EditableCell,
            size: 85,
        }),
        columnHelper.accessor("index_only", {
            id: "index_only",
            header: "Index Only",
            cell: EditableCell,
            size: 85,
        }),
        columnHelper.accessor("delete_building_name_id", {
            id: "delete_building_name_id",
            header: "",
            cell: ({ row }) => (
                <Button type="button" size="sm" variant="danger"
                    onClick={(e) => handleDeleteRow(row, e)}>
                    Delete
                </Button>
            ),
            size: 65,
        }),
    ];

    const tableMeta = {
        updateData: (rowIndex, columnId, value) => {
                updateTableState(dataSet, rowIndex, columnId, value);
        },
            handleSave: async ({ row, column, value }) => {
                const record = {
                    ...(data?.[row.index] || {}),
                    ...row.original,
                    ...(column?.id ? { [column.id]: value } : {}),
                };

            const updatedRows = [...(data || [])];
            updatedRows[row.index] = record;
            const validationMessage = validateRows(updatedRows);
            if (validationMessage) {
                showValidationMessage(validationMessage);
                return false;
            }

            try {
                await saveRecord(dataSet, record);
                console.log("Record saved successfully via parent logic");
            } catch (err) {
                console.error("Save failed:", err);
            }
        },
        handleDelete: ({ row }) => {
            deleteRecord(dataSet, row.original);
        },
    };

    // --- Data Fetching and Update Functions ---
    async function addRecord(event) {
        let localData = [...data];
        //Minimum record to create (all not nulls populated)
        localData.unshift({
            _client_id: `building-name-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            property_id: filter.propertyid,
            building_name: "",
            sort_order: localData.length + 1,
            index_only: false,
            created_by: user,
            last_edited_by: user,
            new_record: true
        });
        updateTableState(localData, dataSet);
    }

    // --- Render Functions ---
    function renderList(data) {
        if (!data) {
            console.log("No data for building names");
            return null
        }
        else {
            const sortedData = [...data]
                .sort((a, b) => Number(a?.sort_order ?? 0) - Number(b?.sort_order ?? 0))
                .sort((a, b) => Number(Boolean(a.index_only)) - Number(Boolean(b.index_only)));
            return [{}].concat(sortedData).map(
                (d, i) =>
                    i !== 0
                        ?
                        <ListGroup.Item key={d.building_name_id || d._client_id || i} as="a" header={d.building_name}>
                            {d.sort_order.toString() + ") " + d.building_name + (d.index_only ? " (index only)" : "")}
                        </ListGroup.Item>
                        : ""
            );
        }
    }

    function renderComponent() {
        return (
            <div className="componentRendering">
                <Modal show={showAlert} onHide={() => setShowAlert(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Uh Oh!</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Alert variant="danger">{errorMessage}</Alert>
                    </Modal.Body>
                </Modal>
                {controlType === "table" && (
                    <Row>
                        <Col className="text-right">
                            <Button variant="secondary" size="sm" onClick={addRecord}>Add</Button>
                        </Col>
                    </Row>
                )}

                <Row className="show-Container">
                    <Col sm={12} md={12} lg={12}>
                        {isLoading ? (
                            <div className="outer-div">
                                <div className="sweet-loading inner-div">
                                    <PacmanLoader
                                        cssOverride={{ display: "block", margin: "0 auto" }}
                                        size={20}
                                        color={"#e03c31"}
                                        loading={isLoading}
                                    />
                                </div>
                            </div>
                        ) : controlType === "table" ? (
                            <GenericTable
                                columns={columns}
                                data={tableData}
                                getRowId={(row, index) => String(row._client_id ?? row.building_name_id ?? index)}
                                section="building-name"
                                pageSize={10}
                                meta={tableMeta}
                            />
                        ) : renderList(data)}
                    </Col>
                </Row>
            </div>
        );
    }
    return (
        <div className="BuildingNameList">
            {isAuthenticated ? renderComponent() : console.log("User not authenticated.")}
        </div>
    );
}

export default BuildingNameList;